// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export enum CompanyType {
  Hosting = 1,
  Customer = 2
}

export interface User {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  companyId: number;
  companyName: string;
  companyType: CompanyType;
  role: string;
}

/**
 * The company a super admin is currently working inside, when it is not their
 * own. Null the rest of the time.
 */
export interface ActingCompany {
  companyId: number;
  companyName: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  registrationCode?: string;
  companyId?: number;
  companyName?: string;
  companyPhone?: string;
  companyAddress?: string;
  companyCity?: string;
  companyPostCode?: string;
}

interface RegisterResponse {
  success: boolean;
  message: string;
  requiresApproval?: boolean;
  requiresEmailConfirmation?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<RegisterResponse>;
  logout: () => void;
  isAuthenticated: boolean;

  /** Where a super admin is working, when it is not their own company. */
  actingAs: ActingCompany | null;
  /** When the acting token stops working. Null unless acting. */
  actingExpiresAt: Date | null;
  startActingAs: (companyId: number) => Promise<ActingCompany | null>;
  stopActing: () => Promise<void>;
}

// localStorage keys. `token` and `user` are the existing ones and keep their
// meaning: `token` is whichever token requests should carry, which while acting
// is the acting one. The rest exist only while acting.
const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const HOME_TOKEN_KEY = 'homeToken';
const ACTING_AS_KEY = 'actingAs';
const ACTING_EXPIRES_KEY = 'actingExpiresAt';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to build headers with API key
const buildAuthHeaders = (includeJwtToken: boolean = false): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add API key (required by ApiKeyMiddleware)
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  } else {
    console.error('API key not found in environment variables');
  }

  // Add JWT token if requested and available
  if (includeJwtToken) {
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [actingAs, setActingAs] = useState<ActingCompany | null>(null);
  const [actingExpiresAt, setActingExpiresAt] = useState<Date | null>(null);

  const clearActingStorage = (): void => {
    localStorage.removeItem(HOME_TOKEN_KEY);
    localStorage.removeItem(ACTING_AS_KEY);
    localStorage.removeItem(ACTING_EXPIRES_KEY);
  };

  /**
   * Drop back to the super admin's own token without asking the API.
   *
   * Used when the acting token has expired, which is exactly the moment a round
   * trip carrying it would fail too. The audit row is left open; stop-acting
   * closes every open row it finds next time it is called, so nothing is lost
   * by not calling it here.
   */
  const returnHomeLocally = (): void => {
    const homeToken = localStorage.getItem(HOME_TOKEN_KEY);
    if (homeToken) {
      localStorage.setItem(TOKEN_KEY, homeToken);
      setToken(homeToken);
    }
    clearActingStorage();
    setActingAs(null);
    setActingExpiresAt(null);
  };

  useEffect(() => {
    // Check for stored auth data on mount
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedToken && storedUser) {
      try {
        const parsedUser: User = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        clearActingStorage();
        setIsLoading(false);
        return;
      }
    }

    // Restore an acting session across a page refresh - losing it on every F5
    // would make it useless halfway through any real piece of work.
    //
    // But only if it is still alive. The acting token lasts an hour, so a tab
    // left open overnight comes back to one the API will reject, and the right
    // thing then is to be home rather than to discover it on the first save.
    // Falling back is safe in a way that carrying on is not.
    const storedActing = localStorage.getItem(ACTING_AS_KEY);
    const storedExpiry = localStorage.getItem(ACTING_EXPIRES_KEY);
    const storedHomeToken = localStorage.getItem(HOME_TOKEN_KEY);

    if (storedActing && storedExpiry && storedHomeToken) {
      try {
        const expiry = new Date(storedExpiry);
        if (expiry.getTime() > Date.now()) {
          setActingAs(JSON.parse(storedActing) as ActingCompany);
          setActingExpiresAt(expiry);
        } else {
          localStorage.setItem(TOKEN_KEY, storedHomeToken);
          setToken(storedHomeToken);
          clearActingStorage();
        }
      } catch (error) {
        console.error('Error restoring acting session:', error);
        localStorage.setItem(TOKEN_KEY, storedHomeToken);
        setToken(storedHomeToken);
        clearActingStorage();
      }
    }

    setIsLoading(false);
  }, []);

  // Come home by ourselves when the hour runs out, rather than leaving a banner
  // on screen that is no longer true and a token every request will be refused
  // with. The timer is rearmed whenever the expiry changes and cleared on the
  // way out, so switching companies twice does not leave two of them running.
  useEffect(() => {
    if (!actingExpiresAt) return;

    const msLeft = actingExpiresAt.getTime() - Date.now();
    if (msLeft <= 0) {
      returnHomeLocally();
      return;
    }

    const timer = window.setTimeout(returnHomeLocally, msLeft);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actingExpiresAt]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Auth/login`, {
        method: 'POST',
        headers: buildAuthHeaders(false), // Don't include JWT for login
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Login failed');
      }

      const data = await response.json();

      setToken(data.token);
      setUser(data.user);

      // A fresh sign-in always starts at home. Leaving acting keys behind would
      // put whoever signs in next inside whichever customer the last session
      // happened to be looking at.
      clearActingStorage();
      setActingAs(null);
      setActingExpiresAt(null);

      // Store in localStorage
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Start working inside another company.
   *
   * The super admin's own token is kept as `homeToken` before the acting one
   * replaces it, so returning is instant and, more importantly, still possible
   * when the acting token has expired - the point at which asking the API for
   * anything would fail.
   */
  const startActingAs = async (companyId: number): Promise<ActingCompany | null> => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/superadmin/act-as/${companyId}`,
      { method: 'POST', headers: buildAuthHeaders(true) }
    );

    if (!response.ok) {
      throw new Error((await response.text()) || 'Could not switch company');
    }

    const data = await response.json();

    // Keep the home token first. If anything below throws, the worst case is a
    // stored token that is still valid rather than one that cannot be recovered.
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken && !localStorage.getItem(HOME_TOKEN_KEY)) {
      localStorage.setItem(HOME_TOKEN_KEY, currentToken);
    }

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);

    if (data.actingAs) {
      localStorage.setItem(ACTING_AS_KEY, JSON.stringify(data.actingAs));
      setActingAs(data.actingAs);
    }

    if (data.expiresAt) {
      localStorage.setItem(ACTING_EXPIRES_KEY, data.expiresAt);
      setActingExpiresAt(new Date(data.expiresAt));
    }

    // Choosing your own company is how you come home, and the API answers that
    // with actingAs: null rather than an error. Honour it here too.
    if (!data.actingAs) {
      clearActingStorage();
      setActingAs(null);
      setActingExpiresAt(null);
    }

    return data.actingAs ?? null;
  };

  /** Return to the super admin's own company. */
  const stopActing = async (): Promise<void> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/stop-acting`,
        { method: 'POST', headers: buildAuthHeaders(true) }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);
        clearActingStorage();
        setActingAs(null);
        setActingExpiresAt(null);
        return;
      }
    } catch (error) {
      console.error('Error returning to own company:', error);
    }

    // The request failed, or the acting token it carried had already expired.
    // Come home on the stored token regardless: being stuck inside somebody
    // else's company because the network blipped is the one outcome worth
    // ruling out entirely.
    returnHomeLocally();
  };

  const register = async (data: RegisterData): Promise<RegisterResponse> => {
    try {
      const requestBody = {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        registrationCode: data.registrationCode,
        companyId: data.companyId,
        companyName: data.companyName,
        companyPhone: data.companyPhone,
        companyAddress: data.companyAddress,
        companyCity: data.companyCity,
        companyPostCode: data.companyPostCode
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/Auth/register`, {
        method: 'POST',
        headers: buildAuthHeaders(false), // Don't include JWT for registration
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Registration failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = (): void => {
    setUser(null);
    setToken(null);
    setActingAs(null);
    setActingExpiresAt(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    clearActingStorage();
  };

  const isAuthenticated = !!token && !!user;

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated,
    actingAs,
    actingExpiresAt,
    startActingAs,
    stopActing,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};